<template>
  <div v-if="currentRoute == 'Questions'">
    <div
      v-hammer:tap="collapseDrawer"
      :class="[
        { 'visible opacity-full' : drawerOpened }
      ]"
      class="backdrop position-fixed vw-100 vh-100 bg-secondary-opacity-9 top-0 left-0 timing-ease speed-base z-plus-11" />
    <div
      @mouseover="mouseoverHandler"
      @mouseleave="mouseleaveHandler"
      :class="[
        { 'drawer--revealed' : drawerRevealed },
        { 'drawer--opened' : drawerOpened },
      ]"
      :style="drawerProperties"
      class="drawer position-fixed left-0 w-100 py-4 bg-muted shadow-type-2 d-flex flex-column align-items-center justify-content-start z-plus-12 mb-auto">
      <div
        v-hammer:swipe="swipeHandler"
        class="drawer__content w-100 px-3 flex-column flex-center">
        <div class="drawer__bar bg-secondary w-25 radius-small mb-4" />
        <div class="d-flex w-100">
          <!-- Avatar -->
          <div
            v-if="isDesktop"
            class="drawer__userpic speed-base timing-ease no-overflow"
            :class="[
              { 'drawer__userpic--hidden' : drawerOpened },
              { 'drawer__userpic--full' : !drawerOpened },
            ]">
            <app-avatar :size="'mini'" />
          </div>
          <!-- // Avatar -->

          <!-- Progress bar -->
          <div class="ingr-progress flex-center flex-column w-100 mb-8">
            <div class="ingr-progress__title font-md font-sm-lg mb-2 text-center text-sm-left w-100">
              Processing of Suitable Ingredients
            </div>
            <div class="ingr-progress__body flex-center w-100">
              <div class="position-relative ingr-progress__bar flex-grow-1 bg-white">
                <div
                  class="ingr-progress__fill h-100 speed-base timing-standard gradient-ingredients_bar"
                  :style="`width: ${ingredientsProgress}%`">
                </div>
              </div>
              <div class="ingr-progress__percentage line-height-1 pl-4">
                {{ displayNumber }}%
              </div>
            </div>
          </div>
          <!-- // Progress bar -->
        </div>
      </div>
      <VuePerfectScrollbar
        class="w-100 drawer__content"
        :style="scrollAreaProperties"
        :options="{
          wheelPropagation: true
        }">

        <!-- Ingredients -->
        <div class="d-flex align-items-center w-100 mb-7 mb-sm-9 px-3">
          <app-avatar
            v-if="isDesktop"
            :size="'full'"
            class="mr-8" />
          <div
            v-if="userIngredientsLength > 0"
            class="ingredients d-flex flex-wrap w-100">
            <div
              v-for="ingredient in userIngredients"
              :key="`ingredient-${ingredient.id}`"
              class="ingredient w-50 w-sm-33 d-flex my-1 my-sm-2">
              <svg-icon
                :icon="ingredient.id"
                size="md"
                class="mr-2" />
              <span
                class="font-sm font-sm-md"
                style="padding-top: 3px">{{ ingredient.name }}</span>
            </div>
          </div>
          <div
            v-else
            class="flex-center text-center w-100">
            <svg-icon
              icon="no-data"
              size="md"
              class="mr-2" />
            <span class="clr-secondary-desat-2">No Data Available at the Moment</span>
          </div>
        </div>
        <!-- // Ingredients -->

        <!-- Avatar -->
        <app-avatar
          v-if="isMobile"
          :size="'full'"
          class="mb-6" />
        <!-- // Avatar -->

        <!-- Info -->
        <div class="d-flex flex-column w-100 px-3 mb-3">
          <div class="font-lg mb-4 text-center w-100 font-weight-600">
            The Quiz Result Will Show Following Concerns
          </div>
          <div class="flex-center flex-wrap w-100">
            <div
              v-for="concern in concerns"
              :key="concern.icon"
              class="flex-center flex-column text-center w-33 w-sm-auto mb-4 mx-0 mx-sm-4">
              <svg-icon
                :icon="concern.icon"
                size="xl"
                class="mb-1" />
              <span>{{ concern.title }}</span>
            </div>
          </div>
        </div>
        <!-- Info -->

        <!-- // Close button -->
        <div
          v-if="isMobile"
          class="px-3">
          <button
            class="button button--outline"
            :class="[
              { 'button--block' : currentBreakpoint == 'xs' }
            ]"
            @click="collapseDrawer()">
            Close
          </button>
        </div>
        <!-- // Close button -->

      </VuePerfectScrollbar>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Drawer',
  data() {
    return {
      drawerOpened: false,
      drawerRevealed: false,
      drawerHovered: false,
      hoverOpenDelay: 250, // ms
      revealDelay: 1750, // ms
      drawerHeight: 0, // px, will be updated
      revealGap: 100, // px
      maxVisibleIngredients: 6,

      displayNumber: 0,

      concerns: [
        {
          icon: 'acne',
          title: 'Acne',
        },
        {
          icon: 'sensitiveness',
          title: 'Sensitiveness',
        },
        {
          icon: 'wrinkles',
          title: 'Wrinkles',
        },
        {
          icon: 'radiance',
          title: 'Radiance',
        },
        {
          icon: 'comedones',
          title: 'Comedones',
        },
        {
          icon: 'redness',
          title: 'Redness',
        },
      ],
    }
  },
  computed: {
    userUid() {
      return this.$store.state.user.uid
    },

    windowSize() {
      /// Returns current window width and height
      ///
      /// @example
      ///   {"width": 768, "height": 493 }
      ///   {"width": 1043, "height": 764 }
      ///   {"width": 1920, "height": 812 }

      return this.$store.state.config.windowSize
    },

    windowHeight() {
      return this.windowSize.height
    },

    currentBreakpoint() {
      /// Returns current 'morphological' breakpoint value based on window width
      ///
      /// @example
      ///   'xs' | 'sm' | 'md' | 'lg' | 'xl'

      return this.$store.state.config.currentBreakpoint
    },

    isDesktop() {
      return this.currentBreakpoint == 'md' || this.currentBreakpoint == 'lg' || this.currentBreakpoint == 'xl'
    },

    isMobile() {
      return this.currentBreakpoint == 'xs' || this.currentBreakpoint == 'sm'
    },

    currentRoute() {
      return this.$route.name
    },

    drawerProperties() {
      if (this.drawerRevealed) {
        return {
          height: `${this.drawerHeight}px`,
          bottom: `-${this.drawerHeight - this.revealGap}px`,
        }
      } else if (this.drawerOpened) {
        return {
          height: `${this.drawerHeight}px`,
          bottom: '0px',
        }
      } else {
        return ''
      }
    },

    scrollAreaProperties() {
      return `max-height: ${this.drawerHeight - 98}px`
    },

    ingredientsVocab() {
      return this.$store.state.user.ingredientsVocab
    },

    receivedIngredients() {
      return this.$store.state.user.ingredients
    },

    userIngredients() {
      const results = []

      Object.keys(this.ingredientsVocab).forEach(key => {
        if (this.receivedIngredients.some(i => i == key)) {
          results.push({
            id: key,
            name: this.ingredientsVocab[key].name
          })
        }
      })

      return results.reverse().slice(0, this.maxVisibleIngredients)
    },

    userIngredientsLength() {
      return this.userIngredients.length
    },

    ingredientsProgress() {
      return this.$store.state.user.ingredientsProgress
    },
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    currentRoute(newVal, oldVal) {
      if (newVal == 'Questions') {
        const self = this
        setTimeout(() => {
          self.drawerRevealed = true
        }, self.revealDelay)
      }
    },

    windowHeight() {
      if (this.isDesktop) {
        this.setDrawerHeight()
      }
    },

    ingredientsProgress() {
      clearInterval(this.interval)

      if (this.ingredientsProgress == this.displayNumber) {
        return
      }

      this.interval = window.setInterval(() => {
        if (this.displayNumber != this.ingredientsProgress) {
          var change = (this.ingredientsProgress - this.displayNumber) / 10
          change = change >= 0 ? Math.ceil(change) : Math.floor(change)
          this.displayNumber = this.displayNumber + change
        }
      }, 50)
    }
  },
  mounted() {
    const self = this

    setTimeout(() => { self.setDrawerHeight() }, 300)

    this.displayNumber = this.ingredientsProgress ? this.ingredientsProgress : 0
  },
  methods: {
    swipeHandler(event) {
      if (event.angle < -90 || event.angle < 0) {
        this.expandDrawer()
      } else if (event.angle < 90 || event.angle > 90) {
        this.collapseDrawer()
      }
    },

    expandDrawer() {
      this.drawerRevealed = false
      this.drawerOpened = true
      document.body.classList.add('body--locked')
    },

    collapseDrawer() {
      this.drawerOpened = false
      this.drawerRevealed = true
      document.body.classList.remove('body--locked')
    },

    setDrawerHeight() {
      if (this.isMobile) {
        this.drawerHeight = Math.ceil(this.windowSize.height - this.revealGap)
      } else if (this.isDesktop) {
        this.drawerHeight = Math.ceil(this.windowSize.height / 1.5 - this.revealGap)
      }
    },

    mouseoverHandler() {
      if (this.isDesktop) {
        const self = this
        this.drawerHovered = true
        setTimeout(() => {
          if (self.drawerHovered) {
            self.expandDrawer()
          }
        }, self.hoverOpenDelay)
      }
    },

    mouseleaveHandler() {
      if (this.isDesktop) {
        this.drawerHovered = false
        this.collapseDrawer()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/@core/globals';

.drawer {
  will-change: bottom;
  bottom: -100%;
  transition: transition(bottom, slow, accelerated);

  &--revealed {
    transition: transition(bottom, slow, emphasized);
  }

  &--opened {
    transition: transition(bottom, slower, standard);
  }

  &__content {
    max-width: 700px;
  }

  &__bar {
    max-width: 110px;
    height: 4px;
  }

  &__userpic {
    &--full {
      width: 70px !important;
    }

    &--hidden {
      width: 0 !important;
    }
  }
}

.backdrop {
  visibility: hidden;
  opacity: opacity(0);
}

.ingr-progress {
  &__bar {
    height: 10px;
  }

  &__fill {
    @include animation(
      $name: moving-gradient,
      $duration: 2.4s,
      $count: infinite,
      $timing: linear,
    );

    background-size: 50% 100%;
  }
}
</style>
